import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Empty, Inline, Primary, Secondary, Sheet, Tag, money, useQuery } from "@/components/backoffice/kit";
import ProductEdit from "@/components/backoffice/ProductEdit";
import GenericProductForm from "@/components/backoffice/GenericProductForm";
import { ReorderCards } from "@/components/backoffice/ProductsList";

/** 8.12 Catalog — the master products every shop inherits: photo, name, from price, Locked / Price not confirmed, eye, used by N shops. */
export default function CatalogScreen() {
    const { params, set, pathname } = useQuery();
    const edit = params.get("product");
    const [data, setData] = useState(null);
    const [used, setUsed] = useState({ map: {}, shops: 0 });
    const [err, setErr] = useState("");
    const [add, setAdd] = useState(false);
    const [reorder, setReorder] = useState(false);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => {
        setErr("");
        api.get("/store/catalog").then(({ data: d }) => setData(d)).catch((e) => { setData({ products: [] }); setErr(formatApiError(e.response?.data?.detail)); });
        api.get("/admin/catalog").then(({ data: d }) => setUsed({ map: Object.fromEntries((d.products || []).map((p) => [p.slug, p.used_by])), shops: d.shops || 0 })).catch(() => { });
    }, []);
    useEffect(() => { load(); }, [load]);
    const patch = async (p, body, ok) => {
        try {
            await api.patch(`/store/catalog/${p.kind}/${p.id}`, body);
            if (ok) toast.success(ok);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const eye = (p) => patch(p, p.active ? { simple_visible: !(p.simple_visible !== false) } : { active: true, simple_visible: true });
    const saveOrder = async (keys) => {
        try { await api.put("/store/catalog/order", { card_order: keys }); toast.success("Default card order saved"); setReorder(false); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const push = async () => {
        setBusy(true);
        try { const { data: d } = await api.post("/store/catalog/push"); toast.success(d.total ? `${d.total} product${d.total === 1 ? "" : "s"} pushed to shops (hidden until they switch them on)` : "Every shop already has the whole catalog"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };

    if (data === null) return <div className="flex min-h-[30vh] items-center justify-center" data-testid="catalog-loading"><Loader2 className="animate-spin" /></div>;
    const products = data.products || [];
    const back = `${pathname}?tab=catalog`;
    if (edit) {
        const [kind, id] = edit.split(":");
        const p = products.find((x) => x.kind === kind && x.id === id);
        if (!p) return <div className="grid gap-3"><Secondary to={back} testid="product-back">Back</Secondary><Empty title="Product not found." /></div>;
        return <ProductEdit product={p} consignment={data.consignment} isHome back={back} onSaved={() => { load(); set({ tab: "catalog", product: null }); }} />;
    }
    return (
        <div className="grid gap-4" data-testid="catalog-screen">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-[22px] font-semibold">Catalog <span className="text-[16px] font-normal text-[#4d4d4d]">{products.length} master products · {used.shops} shops</span></h1>
                <div className="flex gap-2">
                    <Secondary onClick={push} disabled={busy} testid="catalog-push">Push to shops</Secondary>
                    <Primary onClick={() => setAdd(true)} testid="catalog-add">Add product</Primary>
                </div>
            </div>
            <p className="text-[14px] text-[#4d4d4d]">Every shop inherits these at the standard price. A locked price comes from QuickBooks; change it only on purpose.</p>
            <Inline error={err} onRetry={load} testid="catalog-error" />
            {products.length === 0 ? <Empty title="No master products yet." testid="catalog-empty" /> : (
                <ul className="grid gap-2" data-testid="catalog-rows">
                    {products.map((p) => {
                        const visible = p.active && p.simple_visible !== false;
                        return (
                            <li key={p.id} className="grid grid-cols-[56px_minmax(0,1fr)] gap-3 border border-[#1a1a1a] bg-white p-3 sm:grid-cols-[72px_minmax(0,1fr)_auto]" data-testid={`catalog-row-${p.id}`}>
                                <button onClick={() => set({ tab: "catalog", product: `${p.kind}:${p.id}` })} className="h-14 w-14 border border-[#767676] sm:h-[72px] sm:w-[72px]" aria-label={`Edit ${p.name}`} data-testid={`catalog-open-${p.id}`}>
                                    {p.image && <img src={p.image} alt="" className="h-full w-full object-contain" />}
                                </button>
                                <button onClick={() => set({ tab: "catalog", product: `${p.kind}:${p.id}` })} className="min-w-0 text-left" data-testid={`catalog-name-${p.id}`}>
                                    <p className="truncate text-[16px] font-semibold">{p.name}</p>
                                    <p className="text-[15px]">from {money(p.from_price)} <span className="text-[#4d4d4d]">· used by {used.map[p.slug] ?? "—"} shop{used.map[p.slug] === 1 ? "" : "s"}</span></p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {p.price_locked ? <Tag tone="ink" testid={`catalog-locked-${p.id}`}>Locked</Tag> : <Tag tone="red" testid={`catalog-unconfirmed-${p.id}`}>Price not confirmed</Tag>}
                                        {!visible && <Tag tone="grey">Hidden in Simple</Tag>}
                                    </div>
                                </button>
                                <div className="col-span-2 flex items-center sm:col-span-1 sm:justify-self-end">
                                    <button onClick={() => eye(p)} aria-pressed={visible} data-testid={`catalog-eye-${p.id}`}
                                        className="inline-flex min-h-[44px] items-center gap-1.5 border border-[#1a1a1a] bg-white px-2 text-[14px] font-medium">
                                        {visible ? <><Eye size={16} /> Shown</> : <><EyeOff size={16} /> Hidden</>}
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            {reorder ? <ReorderCards cards={data.cards || []} onDone={saveOrder} />
                : (data.cards || []).length > 1 && <button onClick={() => setReorder(true)} className="min-h-[44px] justify-self-start underline" data-testid="catalog-reorder">Reorder cards (default for new shops)</button>}
            {add && (
                <Sheet title="Add a master product" onClose={() => setAdd(false)} testid="catalog-add-sheet">
                    <p className="mb-3 text-[14px] text-[#4d4d4d]">New and existing shops receive it through the catalog (hidden until each shop switches it on).</p>
                    <GenericProductForm showFulfillment={false} onSaved={() => { setAdd(false); load(); toast.success("Master product added"); }} />
                </Sheet>
            )}
        </div>
    );
}
